import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

/**
 * Returns a setter that writes multiple URL search-param keys in ONE setSearchParams call.
 * Use this when an event handler needs to update 2+ URL keys synchronously — back-to-back
 * single-key setters race because react-router updates its internal searchParamsRef in a
 * post-commit useEffect, so the second call's `prev` is stale.
 *
 * - Pass `null` as a value to delete the key.
 * - Other values are written as `String(value)` and replace any existing key.
 * - Pass `opts.replace = true` for history-replace semantics (no new Back-stack entry).
 *   User-gesture callers omit this (default false = push). The prune effect passes true.
 */
export function useSetMultipleSearchParams(): (
  next: Record<string, string | null>,
  opts?: { replace?: boolean },
) => void {
  const [, setSearchParams] = useSearchParams()
  return useCallback(
    (next, opts) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          for (const [key, value] of Object.entries(next)) {
            if (value === null) {
              params.delete(key)
            } else {
              params.set(key, value)
            }
          }
          return params
        },
        { replace: opts?.replace ?? false },
      )
    },
    [setSearchParams],
  )
}
